import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AddArticleRequestForm } from '../addArticleRequestForm';
import { ModifyOrderLineModalComponent } from './types';
import Modal from 'components/base/Modal';

import { OrderLine, OrderLineChannel } from 'config/apiFunus/generated/data-contracts';
import { useArticles } from 'hooks/useArticles';
import { OrderDraft } from 'models/OrderDraft';
import { showErrorToast } from 'utils/toasts';

export const ModifyOrderLine: FC<ModifyOrderLineModalComponent> = ({
  show,
  value,
  articleId,
  onSuccess,
  onHide,
}) => {
  const {
    fetchArticleProviders, articleProviders, getArticleDraft,
  } = useArticles();
  const { t } = useTranslation();
  const [payload, setpayload] = useState<OrderLine & {idSupplier?: string}>();
  const [selectedArticle, setselectedArticle] = useState<string>();
  const [isFormReady, setIsFormReady] = useState<boolean>(false);
  const [articleDraft, setArticleDraft] = useState<OrderDraft>();

  useEffect(
    () => {
      if (articleId && show) {
        getArticleDraft(articleId)
          .then((response) => {
            setArticleDraft(response);
            return response;
          }).catch(() => false);
      } else if (!articleId) {
        setArticleDraft(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleId, show],
  );

  useEffect(
    () => {
      if (selectedArticle) {
        fetchArticleProviders(selectedArticle)
          .catch((error) => {
            showErrorToast(error.message);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedArticle],
  );

  useEffect(
    () => {
      if (articleId && show) {
        setpayload(value);
      } else if (!articleId) {
        setpayload(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleId, show],
  );

  const acceptRequest = useCallback(
    async () => {
      if (payload && articleId) {
        const supplier = articleProviders.find((s) => s.code === payload.idSupplier);
        const requestBody = {
          amount: 1,
          articleDescription: payload.articleDescription,
          category: payload.category,
          channel: OrderLineChannel.PROCESSING,
          client: payload.client,
          code: payload.code,
          comments: payload.comments,
          date: payload?.date ? new Date(payload?.date) : undefined,
          dedicatory: payload.dedicatory,
          erpId: payload.erpId,
          idSupplier: supplier?.code || '',
          observations: payload.observations,
          price: payload.price,
          supplier: supplier?.email || '',
          time: payload.time,
        };
        onSuccess?.(articleId, requestBody);
        setpayload(undefined);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [articleId, articleProviders, onSuccess, payload, t],
  );

  const onClose = useCallback(
    () => {
      setpayload(undefined);
      onHide();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <Modal
      buttons={[
        {
          color: 'secondary',
          onClick: () => {
            onClose();
          },
          text: t('common.cancel'),
        },
        {
          disabled: !isFormReady,
          onClick: acceptRequest,
          text: t('common.accept'),
        },
      ]}
      closeOnFocusLost={false}
      show={show}
      showCancel={false}
      title={t('article.flowers.editDraft')}
      onHide={() => {
        onClose();
      }}
    >
      {payload && articleDraft?.recordId
        ? (
          <AddArticleRequestForm
            channel={OrderLineChannel.PROCESSING}
            clientDisabled={false}
            erpId={value.erpId}
            id={articleDraft?.recordId}
            priceDisabled={false}
            providerDisabled={false}
            showArticleSelector={false}
            showCategorySelector={false}
            value={{
              ...payload,
              amount: 1,
              date: payload.date ? new Date(payload.date) : undefined,
              idSupplier: payload.idSupplier,
              price: payload.price,
              supplier: payload.supplier,
              time: payload.time ? new Date(payload.time) : undefined,
            }}
            vigilDate={payload.date?.toString()}
            vigilHour={payload.time?.toString()}
            articleDisabled
            articleTypeDisabled
            showArticleDescription
            showClientSelector
            onArticleChange={() => false}
            onChange={(p) => {
              setselectedArticle(p.code);
              setpayload(p);
            }}
            onIsValidUpdate={(isValid) => {
              setIsFormReady(isValid);
            }}
            onSupplierChange={() => false}
          />
        )
        : <React.Fragment />}
    </Modal>
  );
};
